import React from 'react';
import type { TriageItem } from '../../triage/types';
import { EmptyState } from '../EmptyState/EmptyState';
import { Icon } from '../Icon/Icon';

export interface TriageNotesCardProps {
  item: TriageItem;
  noteText: string;
  setNoteText: (value: string) => void;
  onAdd: () => void;
  cannedSnippets?: string[] | null;
}

export function TriageNotesCard({
  item,
  noteText,
  setNoteText,
  onAdd,
  cannedSnippets,
}: TriageNotesCardProps) {
  const addSnippet = (txt: string) => {
    setNoteText(noteText?.trim() ? `${noteText.replace(/\s*$/, ' ')}${txt}` : txt);
  };

  return (
    <section className="v6-card v6-fade-up v6-d-4" style={{ marginBottom: 14 }}>
      <header
        style={{
          padding: '14px 20px 12px',
          borderBottom: '1px solid var(--cream-2)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <Icon kind="doc" size={14} stroke={2} color="color-mix(in srgb, var(--gold) 78%, #000)" />
        <span className="v6-display" style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-0)', letterSpacing: '-0.01em' }}>
          Notes
        </span>
        {item.notes.length > 0 ? (
          <span className="v6-mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            {item.notes.length}
          </span>
        ) : null}
      </header>

      <div style={{ padding: '14px 20px 16px' }}>
        {item.notes.length > 0 ? (
          <div style={{ marginBottom: 14 }}>
            {item.notes.map((note, i) => (
              <div
                key={`${note.author}-${note.time}-${i}`}
                style={{
                  padding: '11px 14px',
                  background: 'color-mix(in srgb, var(--cream-1) 62%, var(--cream-0))',
                  border: '1px solid var(--cream-2)',
                  borderRadius: 9,
                  marginBottom: i < item.notes.length - 1 ? 8 : 0,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-0)', letterSpacing: '-0.005em' }}>
                    {note.author}
                  </span>
                  <span className="v6-mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
                    {note.time}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--ink-1)', lineHeight: 1.55, letterSpacing: '-0.005em' }}>
                  {note.text}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            size="sm"
            variant="box"
            icon="pencil"
            title="No notes yet"
            sub="Add a note below."
            className="mb-3 py-[var(--sp-5)]"
          />
        )}

        {cannedSnippets && cannedSnippets.length > 0 ? (
          <div style={{ marginBottom: 10 }}>
            <div className="v6-eyebrow" style={{ fontSize: 9.5, marginBottom: 6 }}>
              Quick add to note
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {cannedSnippets.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => addSnippet(s)}
                  style={{
                    padding: '5px 11px',
                    borderRadius: 999,
                    fontSize: 11.5,
                    fontWeight: 500,
                    background: 'var(--cream-1)',
                    color: 'var(--ink-1)',
                    border: '1px solid var(--cream-shadow)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-body)',
                    letterSpacing: '-0.005em',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  <Icon kind="doc" size={10} stroke={2} color="var(--ink-3)" />
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 8 }}>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Write a note… (e.g., 'Called patient, scheduled for tomorrow 9 am')"
            rows={2}
            style={{
              flex: 1,
              padding: '9px 12px',
              border: '1px solid var(--cream-2)',
              borderRadius: 8,
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              color: 'var(--ink-0)',
              letterSpacing: '-0.005em',
              lineHeight: 1.55,
              outline: 'none',
              resize: 'vertical',
              background: 'var(--cream-1)',
              boxShadow: 'var(--sh-1)',
            }}
          />
          <button
            type="button"
            onClick={onAdd}
            disabled={!noteText.trim()}
            style={{
              alignSelf: 'flex-end',
              height: 32,
              padding: '0 14px',
              borderRadius: 8,
              fontSize: 12.5,
              fontWeight: 600,
              background: noteText.trim() ? 'var(--success)' : 'var(--surface-mute)',
              color: noteText.trim() ? 'var(--cream-1)' : 'var(--ink-3)',
              border: noteText.trim()
                ? '1px solid color-mix(in srgb, var(--success) 80%, #000)'
                : '1px solid var(--cream-2)',
              cursor: noteText.trim() ? 'pointer' : 'default',
              fontFamily: 'var(--font-body)',
              letterSpacing: '-0.005em',
            }}
          >
            Add note
          </button>
        </div>
      </div>
    </section>
  );
}
